import { useFormContext } from 'react-hook-form';
import type { FC } from 'react';
import { Box, styled, Typography } from '@mui/material';
import type { PackEntry } from 'types/shared';
import type { AdSetEntry } from '../api/getAdSets';
import { useAdSetBudgetSums } from '../hooks/useAdSetBudgetSums';

const StyledTypography = styled(Typography)(({ theme, variant }) => ({
  color: 'rgba(57, 32, 128, 1)',
  fontSize:
    variant === 'body1'
      ? theme.typography.body1.fontSize
      : theme.typography.caption.fontSize,
  fontWeight: variant === 'body1' ? 500 : 400,
}));

const AVAILABLE_STEPS = [3, 4, 5];
type WatchFormDataType = [
  number,
  AdSetEntry[],
  Record<string, string>,
  Record<string, string>,
  Record<string, string>,
  PackEntry[],
];

const AdSetBudgetCounter: FC = () => {
  const { watch } = useFormContext();

  const formData: WatchFormDataType = watch([
    'currentStep',
    'selectedAdSets',
    'selectedAdSetsBudgets',
    'selectedCampaignsBudgets',
    'selectedAdSetsDuplicatesCount',
    'packsWithCreatives',
  ]);

  const [
    currentStep,
    selectedAdSets,
    budgets,
    campaignBudgets,
    duplicateCounts,
    packsWithCreatives,
  ] = formData;

  const { totalSum } = useAdSetBudgetSums({
    selectedAdSets,
    budgets,
    campaignBudgets,
    duplicateCounts,
  });
  const isStepThree = currentStep === 3;
  const displayAmount = isStepThree
    ? totalSum
    : totalSum * packsWithCreatives.length;
  const displayLabel = isStepThree ? '/ pack' : 'Total';

  if (!AVAILABLE_STEPS.includes(currentStep)) {
    return null;
  }

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        justifyContent: 'flex-start',
        backgroundColor: theme.palette.info.dark,
        px: 2,
        borderRadius: 1,
      })}
    >
      <StyledTypography variant="body1">${displayAmount}</StyledTypography>
      <StyledTypography variant="caption">{displayLabel}</StyledTypography>
    </Box>
  );
};

export default AdSetBudgetCounter;
